import { ColumnDef } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import Table, { TableFooter, buildCopyTextColumn } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import type { ElectionPastPresidentAssigneeNode } from "../../queries";

export type PastPresidentAssigneesTableProps = {
  loading: boolean;
  assignees: ElectionPastPresidentAssigneeNode[];
  busy: boolean;
  rosterCountLabel: string;
  onAdd: () => void;
  onRemove: (assignee: ElectionPastPresidentAssigneeNode) => void;
};

function buildColumns(
  busy: boolean,
  assigneeCount: number,
  onRemove: (assignee: ElectionPastPresidentAssigneeNode) => void
): ColumnDef<ElectionPastPresidentAssigneeNode, unknown>[] {
  return [
    {
      id: "name",
      header: "Past President",
      accessorFn: (row) => row.name,
      cell: ({ row }) => row.original.name,
      enableSorting: true
    },
    buildCopyTextColumn<ElectionPastPresidentAssigneeNode>({
      accessorKey: "pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.pgaId ?? "",
      shouldShowCopyButton: (row) => Boolean(row.pgaId),
      size: 120
    }),
    {
      id: "email",
      header: "Email",
      accessorFn: (row) => row.email,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      enableSorting: true,
      size: 220
    },
    {
      id: "remove",
      header: "",
      size: 56,
      minSize: 56,
      maxSize: 56,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <IconButton
            name="delete"
            ariaLabel={`Remove ${row.original.name}`}
            onClick={() => onRemove(row.original)}
            disabled={busy || assigneeCount <= 1}
            data-testid={`remove-past-president-${row.original.id}`}
          />
        </div>
      )
    }
  ];
}

export default function PastPresidentAssigneesTable({
  loading,
  assignees,
  busy,
  rosterCountLabel,
  onAdd,
  onRemove
}: PastPresidentAssigneesTableProps) {
  return (
    <Table
      loading={loading}
      data={assignees}
      columns={buildColumns(busy, assignees.length, onRemove)}
      data-testid="past-president-assignees-table"
      renderNoDataEmptyState={() => (
        <EmptyState
          iconName="groups"
          title="No Past Presidents on the List"
          caption="Add at least one Past President to generate a roster."
          action={{ onClick: onAdd, icon: "add", children: "Add Past President", disabled: busy }}
        />
      )}
      renderFooter={() => (
        // Entity-named count (not totalRowCount "# rows") — intentional for Past Presidents roster copy.
        <TableFooter
          pagination={
            <div className="text-label-large" data-testid="past-presidents-roster-count">
              {rosterCountLabel}
            </div>
          }
        />
      )}
    />
  );
}
